import type { JSX } from 'react';

import { CheckCircleOutlineIcon } from '@change/ds-icons';

import { Button, Text } from '@change/ds-core-ui/next';

import { Translate } from '@change-corgi/core/react/i18n';

import { useMarkModuleAsLoaded } from 'src/app/pages/starterDashboardV2/shared/contexts';

import { Loading } from './components/Loading';
import { ShareVisualContent } from './components/ShareVisualContent/index_new';
import { ToastBanner } from './components/ToastBanner';
import { useShareVisual } from './hooks/useShareVisual';

export function ShareVisual(): JSX.Element | null {
	const {
		data: { buttonDisabled, petition, shareVisualEnabled, shareVisualsState, shareVisualDownloaded },
		actions,
	} = useShareVisual();

	useMarkModuleAsLoaded('ShareVisual', shareVisualEnabled);

	if (!shareVisualEnabled) return null;

	if (shareVisualsState === 'loading') return <Loading />;

	if (shareVisualsState === 'timeout') {
		return (
			<div
				className="bg-background-base p-12"
				data-qa="grow-petition-share-visual-container"
				data-testid="grow-petition-share-visual-timeout-container"
			>
				<Text as="p" className="text-typography-strong">
					<Translate value="corgi.petition.starter_dashboard_v2.shared.share_visuals.modal_timeout" />
				</Text>
			</div>
		);
	}

	return (
		<section
			data-qa="grow-petition-share-visual-container"
			data-testid="grow-petition-share-visual-loaded-container"
			className="bg-background-base flex flex-col gap-0 py-8 pl-4 md:p-12"
		>
			<div className="flex flex-col gap-3 pr-4">
				<Text
					as="h3"
					fontWeight="bold"
					id="share-petition-visuals-title"
					data-qa="share-petition-visuals-title"
					className="text-typography-strong text-size-heading-small"
				>
					<Translate value="corgi.petition.starter_dashboard.grow_petition.share_visuals.title" />
				</Text>
				<Text as="p">
					<Translate value="corgi.petition.starter_dashboard.grow_petition.share_visuals.description" />
				</Text>
			</div>
			<form onSubmit={actions.onShareVisualDownload} data-qa="share-visual-modal-form" className="flex flex-col">
				<ShareVisualContent
					className="gap-0 px-0 py-0 md:pb-8"
					petition={petition}
					context="starter_dashboard_grow_petition"
					onChange={actions.onShareVisualSelect}
				/>
				<div className="flex flex-row gap-6 pr-4">
					<Button
						className="text-typography-strong w-full min-w-[104px] md:w-[160px]"
						variant="secondary"
						size="small"
						disabled={buttonDisabled}
						type="submit"
						data-qa="share-visual-modal-download"
					>
						<div className="hidden md:flex">
							<Translate value="corgi.petition.starter_dashboard_v2.shared.share_visuals.modal_action_button_desktop" />
						</div>
						<div className="flex md:hidden">
							<Translate value="corgi.petition.starter_dashboard_v2.shared.share_visuals.modal_action_button_mobile" />
						</div>
					</Button>
				</div>
			</form>
			{shareVisualDownloaded && (
				<div className="pr-4 pt-6">
					<ToastBanner
						data-testid="grow-petition-share-visual-downloaded"
						className="bg-fill-success-weaker border-stroke-success-weak rounded-lg border px-4 py-3"
					>
						<CheckCircleOutlineIcon className="text-icon-success" size={24} />
						<Text className="text-typography-success" fontWeight="bold">
							<div className="hidden md:flex">
								<Translate value="corgi.petition.starter_dashboard_v2.shared.share_visuals.downloaded_desktop" />
							</div>
							<div className="flex md:hidden">
								<Translate value="corgi.petition.starter_dashboard_v2.shared.share_visuals.downloaded_mobile" />
							</div>
						</Text>
					</ToastBanner>
				</div>
			)}
		</section>
	);
}
